// A21 — THE MENTION FILTER. Brief 65 (brief 50 §4), the sixteenth of the sixteen.
//
// Which chat messages count as an unread mention of me: newer than my last read
// of that room, not written by me, and naming either my initials or everybody.
//
//   src/App.tsx:15947-15950@8fdbbd6c x src/App.tsx:15986-15989@8fdbbd6c  91.2%
//
// ONE DIFFERENCE, ONE PARAMETER (§2): the read timestamp. One copy filters a
// case chat against that case's own last-read mark, the other filters the
// general chat against `gchat:<user>`. The predicate is otherwise the same
// three tests in the same order.
//
// ── AND THE SECOND COMMIT, WHICH IS WHAT RULE 3 REQUIRES ────────────────────
// The move above carried a substring test — `text.includes("@all")` and
// `text.includes("@" + myInitials)`, exactly as both copies did — so `@allan`
// notified the whole firm and initials `LA` matched inside `@Laurent`, `@Lars`
// and `@LAB`. Registered 65c rather than silently corrected, because whether
// mentions are word-bounded is a product question about a firm's chat. Ruled
// word-bounded and case-insensitive on 2026-08-11 (brief 66 §3).
//
// ── AND THE THIRD, WHICH IS WHERE THE RULE STOPPED LIVING HERE ──────────────
// WHAT A MENTION IS NOW LIVES IN src/lib/mentionTokens.mjs, because this was
// never the only plane answering that question: server/src/routes/
// chatMessages.js warns at send time that a mentioned colleague is off the
// recipient set, and it had matched `/@(\w+)/g` — ASCII, unbounded on the left
// — since Phase 30. Two hand-maintained copies of one rule is the defect
// (register 66a), so brief 67 §1 collapsed them into one module both planes
// call. This file is now the CLIENT half only: read-state, authorship, and
// which two tokens are mine.
//
// WHAT MOVED OUT, AND WHAT THAT DELETED. The escape table went with it: a
// token no longer reaches a regex constructor at all, so initials are compared
// as a string and carry no pattern meaning. The per-token regex cache went too
// — there is one matcher now, built once at module load, rather than one per
// collaborator. And the empty-token guard stopped being load-bearing: `coIni()`
// returns "" for a collaborator id that has not resolved (src/utils.ts:572),
// which the substring form read as `text.includes("@")`, but a tokeniser cannot
// produce an empty token. The recording asserts that by requiring the mutation
// which removes the guard to replay GREEN.
//
// WHERE THE BOUNDARY LANDS IS THE WHOLE BEHAVIOUR, so it is characterised
// rather than described: recordings/66-mention-bounds, replayed on every PR
// through BOTH planes, with a `--prove-red` that undoes each half of the rule
// in memory and requires the cases naming it to redden.

import { namesToken } from "./mentionTokens.mjs";

/** Does `text` mention ME — my initials, or everybody? The two tokens that are
 *  mine, in one place, because that pairing was itself written twice: this
 *  module and Dashboard's Personal Messages list (`src/views/Dashboard.tsx`),
 *  which brief 65's extraction did not reach and which was still running the
 *  pre-65c substring test when 67 §1 went looking for the second plane and
 *  found a third. */
export const namesMe = (text: string, myInitials: string): boolean =>
  namesToken(text, myInitials) || namesToken(text, "all");

export const isUnreadMention = (
  m: { ts: number; author: unknown; text: string },
  since: number,
  me: unknown,
  myInitials: string,
) => m.ts > since && m.author !== me && namesMe(m.text, myInitials);
